import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, queueCartUpdate } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
export class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #cartRevision = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    this.#cartRevision++;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  async recover() {
    return queueCartUpdate(async () => {
      const revision = this.#cartRevision;
      try {
        const sectionId = this.querySelector('cart-items-component')?.getAttribute('data-section-id');
        if (!sectionId) throw new Error('Cart section missing');

        const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        if (!Number.isFinite(cart.item_count) || !Array.isArray(cart.items)) throw new Error('Invalid cart');

        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error('Cart section unavailable');
        const html = await response.text();
        const freshDialog = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer-component dialog');
        const headingId = freshDialog?.getAttribute('aria-labelledby');
        if (!headingId || !freshDialog?.querySelector('[data-hydration-key="cart-drawer-inner"]')) {
          throw new Error('Cart drawer missing');
        }
        const renderedCount = freshDialog.classList.contains('cart-drawer--empty')
          ? 0
          : Number(freshDialog.querySelector('[ref="cartItemCount"]')?.textContent);
        if (renderedCount !== cart.item_count) throw new Error('Cart section count mismatch');
        await customElements.whenDefined('cart-icon');
        if (revision !== this.#cartRevision) return true;
        await morphSection(sectionId, html);

        for (const icon of document.querySelectorAll('cart-icon')) {
          if (revision !== this.#cartRevision) return true;
          await /** @type {HTMLElement & { renderCartBubble: (count: number, additive: boolean, animate: boolean) => Promise<void> }} */ (
            icon
          ).renderCartBubble(cart.item_count, false, false);
        }
      } catch (error) {
        console.error(error);
        return revision !== this.#cartRevision;
      }
      if (revision !== this.#cartRevision) return true;
      this.open();
      return true;
    }, true);
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
